<template>
  <div class="border rounded-lg p-3 space-y-3">
    <p class="font-semibold">Clone modules into a new session</p>
    <Select v-model="targetSessionId" :options="targetSessions" optionLabel="label" optionValue="value" placeholder="Target session" class="w-full" />
    <p class="text-sm text-gray-600">
      Choose the curriculum courses for each new module. Course labels show their level or year and semester.
      Existing session assignments are preserved. Copies start inactive.
    </p>
    <p class="text-sm text-gray-600">
      Select the destination session first, then filter by the intended level/year, tick the modules to clone
      and choose courses for each. Only ticked modules with at least one course are cloned; empty or unticked
      rows are skipped. Review the new modules’ dates and semester pins before activating them.
    </p>
    <div class="rounded border bg-gray-50 p-3">
      <div class="flex items-center gap-2">
        <Checkbox
          v-model="copyFeeConfiguration"
          inputId="clone-copy-fees"
          binary
          data-testid="clone-copy-fees"
        />
        <label for="clone-copy-fees" class="text-sm font-medium">Copy module fee configuration</label>
      </div>
      <p class="mt-1 text-xs text-gray-600">
        Copies registration and retake fee templates, including legacy fee amounts. Leave this off when the new
        session will use different prices; cloned modules will start without module fees.
      </p>
    </div>
    <Select v-model="level" :options="levels" placeholder="Filter curriculum by level / year" showClear class="w-full" />

    <div class="flex flex-wrap items-center gap-2">
      <InputText
        v-model="moduleFilter"
        placeholder="Filter modules by name…"
        class="flex-1 min-w-[10rem]"
        data-testid="clone-module-filter"
      />
      <Button text size="small" label="Select all" :disabled="!visibleModules.length" @click="setAllIncluded(true)" />
      <Button text size="small" label="Clear" :disabled="!visibleModules.length" @click="setAllIncluded(false)" />
      <span class="text-xs text-gray-600 whitespace-nowrap" data-testid="clone-module-counter">
        {{ readyCount }} of {{ modules.length }} ready to clone
      </span>
    </div>

    <div class="border rounded p-2 bg-gray-50 space-y-1">
      <label class="text-xs font-medium text-gray-700">Apply the same courses to every ticked module</label>
      <div class="flex flex-wrap items-center gap-2">
        <MultiSelect
          v-model="bulkCourses"
          :options="courseOptions"
          optionLabel="label"
          optionValue="id"
          filter
          display="chip"
          :loading="loading"
          placeholder="Pick a shared course set…"
          class="flex-1 min-w-[14rem]"
          data-testid="clone-bulk-courses"
        />
        <Button
          size="small"
          :label="`Apply to ${includedCount} ticked`"
          :disabled="!bulkCourses.length || includedCount === 0"
          data-testid="clone-bulk-apply"
          @click="applyBulk"
        />
      </div>
      <p class="text-[11px] text-gray-500">
        Overwrites each ticked module’s selection. Handy when a whole cohort shares the same course pool —
        tick every module, pick the courses once, then Apply.
      </p>
    </div>

    <p v-if="!visibleModules.length" class="text-xs text-gray-500 italic">No modules match this filter.</p>

    <div class="space-y-2">
      <details
        v-for="module in visibleModules"
        :key="module.id"
        class="border rounded"
        :open="included[module.sequence] === true"
      >
        <summary class="flex items-center gap-2 px-3 py-2 cursor-pointer select-none list-none hover:bg-gray-50">
          <Checkbox
            v-model="included[module.sequence]"
            :binary="true"
            :inputId="`clone-inc-${module.id}`"
            :data-testid="`clone-include-${module.sequence}`"
            @click.stop
            @change="onToggleInclude(module.sequence)"
          />
          <span class="text-sm font-medium flex-1">
            {{ module.sequence }}. {{ module.name }}
          </span>
          <Tag
            v-if="included[module.sequence] && (assignments[module.sequence]?.length ?? 0) > 0"
            :value="`${assignments[module.sequence]?.length ?? 0} course${(assignments[module.sequence]?.length ?? 0) === 1 ? '' : 's'}`"
            severity="success"
          />
          <Tag
            v-else-if="included[module.sequence]"
            value="Pick courses"
            severity="warn"
          />
          <span class="text-xs text-gray-500">▾</span>
        </summary>
        <div class="px-3 pb-3 pt-1">
          <MultiSelect
            v-model="assignments[module.sequence]"
            :options="courseOptions"
            :disabled="!included[module.sequence]"
            optionLabel="label"
            optionValue="id"
            filter
            display="chip"
            :loading="loading"
            placeholder="Choose courses for the new module"
            class="w-full"
          />
          <p v-if="!included[module.sequence]" class="text-xs text-gray-500 mt-1 italic">
            Tick the checkbox above to include this module in the clone.
          </p>
        </div>
      </details>
    </div>

    <p class="text-xs text-gray-500">Additional elective offerings are semester-specific and are not copied. Use Electives on the new modules to choose their offerings.</p>
    <Message v-if="error" severity="error" :closable="false">{{ error }}</Message>
    <Message v-if="success" severity="success" :closable="false">{{ success }}</Message>
    <Button
      :label="`Clone ${readyCount} module${readyCount === 1 ? '' : 's'}`"
      :loading="cloning"
      :disabled="!targetSessionId || readyCount === 0"
      data-testid="clone-module-submit"
      @click="submit"
    />
  </div>
</template>

<script setup lang="ts">
import { useCloneModuleTimetable, type CloneModuleTimetableProps } from '@/composables/useCloneModuleTimetable';

const props = defineProps<CloneModuleTimetableProps>();
const {
  targetSessionId, targetSessions, copyFeeConfiguration, level, levels,
  courseOptions, assignments, error, success, cloning, loading, submit,
} = useCloneModuleTimetable(props);

// Explicit opt-in per module — the composable already skips modules whose
// assignment is empty, so unticking simply clears that row and it drops off
// the request payload without any special backend handling.
const included = reactive<Record<number, boolean>>({});
const moduleFilter = ref('');
const bulkCourses = ref<string[]>([]);

const visibleModules = computed(() => {
  const q = moduleFilter.value.trim().toLowerCase();
  if (!q) return props.modules;
  return props.modules.filter(m => m.name.toLowerCase().includes(q) || String(m.sequence).includes(q));
});

const includedCount = computed(() =>
  props.modules.filter(m => included[m.sequence]).length,
);

const readyCount = computed(() =>
  props.modules.filter(m => included[m.sequence] && (assignments.value[m.sequence]?.length ?? 0) > 0).length,
);

function applyBulk() {
  // Overwrite — mirrors what admins expect from "apply to all": if they wanted
  // to merge they would pick per-row. A confirm dialog would be overkill for an
  // unsubmitted local edit that is trivially reversible.
  const ids = [...bulkCourses.value];
  for (const m of props.modules) {
    if (included[m.sequence]) assignments.value[m.sequence] = [...ids];
  }
}

function onToggleInclude(seq: number) {
  // Clear the row's courses when it is unticked so the composable's
  // length-based skip filter matches what the UI shows.
  if (!included[seq]) assignments.value[seq] = [];
}

function setAllIncluded(state: boolean) {
  for (const m of visibleModules.value) {
    included[m.sequence] = state;
    if (!state) assignments.value[m.sequence] = [];
  }
}

// Reset the include set whenever the source list changes (programme/session
// switch resets assignments upstream).
watch(() => props.modules.map(m => m.id).join(','), () => {
  for (const k of Object.keys(included)) delete included[Number(k)];
});
</script>

<style scoped>
details > summary::-webkit-details-marker { display: none; }
details[open] > summary > span:last-child { transform: rotate(180deg); }
details > summary > span:last-child { display: inline-block; transition: transform 0.15s; }
</style>
